import { QueryKeys } from "../../network/queryKeys";
import { queryClient } from "../../network/queryClient";
import type {
  tGetSaloonAdminsRes,
  tGetSaloonMastersRes,
  tMaster,
  tMasterService,
  tPremiumAccount,
  tSaloonMemberData,
  tUser,
} from "@/types";
import cloneDeep from "lodash/cloneDeep";

const buildAdminsQueryKey = (saloonId?: string) => {
  const base = QueryKeys.saloonDashboardAdmins;
  if (!saloonId) return base;
  return [base, saloonId];
};

const buildMastersQueryKey = (saloonId?: string) => {
  const base = QueryKeys.saloonDashboardMasters;
  if (!saloonId) return base;
  return [base, saloonId];
};

const invalidateMasters = (saloonId?: string) => {
  queryClient.invalidateQueries(buildMastersQueryKey(saloonId));
};
const invalidateAdmins = (saloonId?: string) => {
  queryClient.invalidateQueries(buildAdminsQueryKey(saloonId));
};
const invalidateAll = (saloonId?: string) => {
  invalidateAdmins(saloonId);
  invalidateMasters(saloonId);
};

export const SaloonStaffCacheService = {
  buildAdminsQueryKey,
  buildMastersQueryKey,
  invalidateAdmins,
  invalidateMasters,
  invalidateAll,
  updateAdmin({
    userId,
    saloonId,
    userUpdates,
    employeeUpdates,
    premiumAccountUpdates,
  }: {
    userId: string;
    saloonId: string;
    userUpdates?: Partial<tUser>;
    employeeUpdates?: Partial<tSaloonMemberData>;
    premiumAccountUpdates?: Partial<tPremiumAccount>;
  }) {
    if (!userId || !saloonId) return;
    queryClient.setQueryDataIfCached(
      buildAdminsQueryKey(saloonId),
      (masters: tGetSaloonAdminsRes): tGetSaloonAdminsRes => {
        return masters.map((initialEntry) => {
          if (initialEntry.admin._id !== userId) return initialEntry;
          const entry = cloneDeep(initialEntry);
          if (employeeUpdates) {
            entry.data = { ...entry.data, ...employeeUpdates };
          }
          if (userUpdates) {
            entry.admin = { ...entry.admin, ...userUpdates };
          }
          if (premiumAccountUpdates) {
            entry.admin.premium_account = {
              ...entry.admin.premium_account,
              ...premiumAccountUpdates,
            };
          }
          return entry;
        });
      },
    );
  },
  addMasterService({
    service,
    saloonId,
  }: {
    service: tMasterService;
    saloonId: string;
  }) {
    if (!saloonId || !service) return;
    queryClient.setQueryDataIfCached(
      buildMastersQueryKey(saloonId),
      (masters: tGetSaloonMastersRes): tGetSaloonMastersRes => {
        return masters.map((initialEntry) => {
          if (initialEntry.master._id !== service.master) {
            return initialEntry;
          }
          const entry = cloneDeep(initialEntry);
          entry.master.services.push(service);
          return entry;
        });
      },
    );
  },
  updateMasterService({
    service,
    saloonId,
  }: {
    service: tMasterService;
    saloonId: string;
  }) {
    if (!saloonId || !service) return;
    queryClient.setQueryDataIfCached(
      buildMastersQueryKey(saloonId),
      (masters: tGetSaloonMastersRes): tGetSaloonMastersRes => {
        return masters.map((initialEntry) => {
          if (initialEntry.master._id !== service.master) {
            return initialEntry;
          }
          const entry = cloneDeep(initialEntry);
          const serviceToUpdate = entry.master.services.find(
            (s) => s._id === service._id,
          );
          if (serviceToUpdate) {
            Object.assign(serviceToUpdate, service);
          }
          return entry;
        });
      },
    );
  },
  removeMasterService({
    service,
    saloonId,
  }: {
    service: tMasterService;
    saloonId: string;
  }) {
    if (!saloonId || !service) return;
    queryClient.setQueryDataIfCached(
      buildMastersQueryKey(saloonId),
      (masters: tGetSaloonMastersRes): tGetSaloonMastersRes => {
        return masters.map((initialEntry) => {
          if (initialEntry.master._id !== service.master) {
            return initialEntry;
          }
          const entry = cloneDeep(initialEntry);
          const idxToRemove = entry.master.services.findIndex(
            (s) => s._id === service._id,
          );
          if (idxToRemove !== -1) {
            entry.master.services.splice(idxToRemove, 1);
          }
          return entry;
        });
      },
    );
  },
  updateMaster({
    userId,
    masterId,
    saloonId,
    masterUpdates,
    userUpdates,
    employeeUpdates,
    premiumAccountUpdates,
  }: {
    userId?: string;
    masterId?: string;
    saloonId: string;
    masterUpdates?: Partial<Omit<tMaster, "user" | "services">>;
    userUpdates?: Partial<tUser>;
    employeeUpdates?: Partial<tSaloonMemberData>;
    premiumAccountUpdates?: Partial<tPremiumAccount>;
  }) {
    if (!saloonId) return;
    if (!masterId && !userId) return;
    queryClient.setQueryDataIfCached(
      buildMastersQueryKey(saloonId),
      (masters: tGetSaloonMastersRes): tGetSaloonMastersRes => {
        return masters.map((initialEntry) => {
          if (masterId && initialEntry.master._id !== masterId) {
            return initialEntry;
          }
          if (userId && initialEntry.master.user._id !== userId) {
            return initialEntry;
          }
          const entry = cloneDeep(initialEntry);
          if (employeeUpdates) {
            entry.data = { ...entry.data, ...employeeUpdates };
          }
          if (masterUpdates) {
            entry.master = { ...entry.master, ...masterUpdates };
          }
          if (userUpdates) {
            entry.master.user = { ...entry.master.user, ...userUpdates };
          }
          if (premiumAccountUpdates) {
            entry.master.user.premium_account = {
              ...entry.master.user.premium_account,
              ...premiumAccountUpdates,
            };
          }
          return entry;
        });
      },
    );
  },
  removeMaster({ id, saloonId }: { id: string; saloonId: string }) {
    if (!id || !saloonId) return;
    queryClient.setQueryDataIfCached(
      buildMastersQueryKey(saloonId),
      (masters: tGetSaloonMastersRes): tGetSaloonMastersRes => {
        return masters.filter(({ master }) => master._id !== id);
      },
    );
  },
  removeAdmin({ id, saloonId }: { id: string; saloonId: string }) {
    if (!id || !saloonId) return;
    queryClient.setQueryDataIfCached(
      buildAdminsQueryKey(saloonId),
      (admins: tGetSaloonAdminsRes): tGetSaloonAdminsRes => {
        return admins.filter(({ admin }) => admin._id !== id);
      },
    );
  },
};
